import { ErrorMessage, Spinner } from "@/components";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { usePlacementByIdQuery } from "@/hooks/api/placement.queries";
import {
  ArrowLeft,
  Briefcase,
  Building2,
  Calendar,
  CheckCircle2,
  DollarSign,
  FileText,
  Mail,
  MapPin,
  Phone,
  User,
  Users,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

const PlacementDetailPlaceholder = () => {
  return (
    <div className="flex justify-center py-16">
      <Spinner />
    </div>
  );
};

const ActivityPlacementView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: placement,
    isLoading,
    isError,
  } = usePlacementByIdQuery(id || "");

  if (isLoading) return <PlacementDetailPlaceholder />;
  if (isError || !placement) {
    return (
      <ErrorMessage
        message="Failed to load placement details."
        onRetry={() => navigate("/activities/placement")}
      />
    );
  }

  // Placeholder Breakdown using total package
  const baseSalary = Math.round(placement.package * 0.82); // 82% approx
  const bonus = Math.round(placement.package * 0.13); // 13% approx
  const stock = placement.package - baseSalary - bonus;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/activities/placement")}
            className="mt-1 h-8 w-8 rounded-full border border-gray-200"
          >
            <ArrowLeft size={16} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {placement.role} at Vendor ID: {placement.company}
            </h1>
            <p className="text-gray-500">
              {placement.students.length} Students Placed
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Badge
            variant={placement.status === "confirmed" ? "default" : "secondary"}
            className="bg-black px-3 py-1 text-xs uppercase text-white hover:bg-black/90"
          >
            {placement.status === "offer_pending"
              ? "Offer Pending"
              : placement.status}
          </Badge>
          <Badge
            variant="outline"
            className="border-gray-200 bg-gray-100 px-3 py-1 text-xs text-gray-900"
          >
            {placement.jobType}
          </Badge>
        </div>
      </div>

      {/* Top Metric Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="rounded-2xl border border-gray-100 shadow-sm">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-50 text-green-600">
              <DollarSign size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Annual Package</p>
              <p className="text-lg font-bold text-green-600">
                ${placement.package.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-gray-100 shadow-sm">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
              <Users size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Students Placed</p>
              <p className="text-lg font-bold text-blue-600">
                {placement.students.length}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-gray-100 shadow-sm">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-50 text-purple-600">
              <MapPin size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Location</p>
              <p className="font-semibold text-gray-900">Redmond, WA</p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-gray-100 shadow-sm">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-50 text-orange-600">
              <Calendar size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Joining Date</p>
              <p className="font-semibold text-gray-900">
                {placement.joiningDate
                  ? new Date(placement.joiningDate).toLocaleDateString("en-GB")
                  : "TBD"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Left Column (Now Full Width) */}
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Company Information */}
            <Card className="h-full rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
                <Building2 size={18} className="text-gray-700" />
                <h3 className="text-lg font-bold text-gray-900">
                  Company Information
                </h3>
              </div>
              <CardContent className="space-y-6 p-6">
                <div>
                  <p className="mb-1 text-sm text-gray-500">Company</p>
                  <p className="font-semibold text-gray-900">
                    Vendor ID: {placement.company}
                  </p>
                </div>
                <div>
                  <p className="mb-1 text-sm text-gray-500">Address</p>
                  <p className="text-sm leading-relaxed text-gray-900">
                    One Microsoft Way, Redmond, WA 98052
                  </p>
                </div>
                <hr className="border-gray-100" />
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm text-blue-600">
                    <Mail size={16} />
                    <a
                      href="mailto:careers@microsoft.com"
                      className="hover:underline"
                    >
                      careers@company.com
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Phone size={16} />
                    <span>+1-425-882-8080</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Job Details */}
            <Card className="h-full rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
                <Briefcase size={18} className="text-gray-700" />
                <h3 className="text-lg font-bold text-gray-900">Job Details</h3>
              </div>
              <CardContent className="space-y-6 p-6">
                <div>
                  <p className="mb-1 text-sm text-gray-500">Role</p>
                  <p className="text-lg font-bold text-gray-900">
                    {placement.role}
                  </p>
                </div>
                <div>
                  <p className="mb-1 text-sm text-gray-500">Description</p>
                  <p className="text-sm leading-relaxed text-gray-500">
                    {placement.jobDescription ||
                      "Design, develop, and maintain scalable software solutions. Work with cross-functional teams to deliver high-quality products."}
                  </p>
                </div>
                <hr className="border-gray-100" />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="mb-1 text-xs text-gray-500">Placement Date</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(placement.placementDate).toLocaleDateString(
                        "en-GB"
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-gray-500">
                      Employment Type
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {placement.jobType}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Package Breakdown */}
            <Card className="h-full rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2 px-6 py-4">
                <DollarSign size={18} className="font-bold text-gray-700" />
                <h3 className="text-lg font-bold text-gray-900">
                  Package Breakdown
                </h3>
              </div>
              <CardContent className="space-y-6 px-6 pb-6">
                <div className="rounded-xl border border-green-100/50 bg-green-50 p-4">
                  <p className="mb-1 text-sm text-gray-600">
                    Total Annual Package
                  </p>
                  <p className="text-3xl font-bold text-green-600">
                    ${placement.package.toLocaleString()}
                  </p>
                </div>

                <div className="space-y-4 px-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Base Salary</span>
                    <span className="font-bold text-gray-900">
                      ${baseSalary.toLocaleString()}
                    </span>
                  </div>
                  <hr className="border-gray-50" />
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Performance Bonus</span>
                    <span className="font-bold text-gray-900">
                      ${bonus.toLocaleString()}
                    </span>
                  </div>
                  <hr className="border-gray-50" />
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Stock Options</span>
                    <span className="font-bold text-gray-900">
                      ${stock.toLocaleString()}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Requirements & Benefits */}
            <Card className="h-full rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
                <FileText size={18} className="text-gray-700" />
                <h3 className="text-lg font-bold text-gray-900">
                  Requirements & Benefits
                </h3>
              </div>
              <CardContent className="space-y-6 p-6">
                <div>
                  <h4 className="mb-3 text-sm font-semibold text-gray-900">
                    Requirements
                  </h4>
                  <ul className="ml-1 list-inside list-disc space-y-2 text-sm text-gray-500">
                    {placement.requirements ? (
                      placement.requirements
                        .split("\n")
                        .map((req, i) => <li key={i}>{req}</li>)
                    ) : (
                      <>
                        <li>
                          Bachelor's degree in Computer Science or related
                        </li>
                        <li>
                          Strong programming skills in Java, C++, or Python
                        </li>
                        <li>Understanding of data structures and algorithms</li>
                      </>
                    )}
                  </ul>
                </div>
                <hr className="border-gray-100" />
                <div>
                  <h4 className="mb-3 text-sm font-semibold text-gray-900">
                    Benefits
                  </h4>
                  <ul className="ml-1 list-inside list-disc space-y-2 text-sm text-gray-500">
                    <li>Health, dental, and vision insurance</li>
                    <li>401(k) with company match</li>
                    <li>Flexible work arrangements</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Placement Coordinator */}
          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
              <Users size={18} className="text-gray-700" />
              <h3 className="text-lg font-bold text-gray-900">
                Placement Coordinator
              </h3>
            </div>
            <CardContent className="p-6">
              <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
                <div>
                  <h4 className="font-semibold text-gray-900">
                    Prof. John Davis
                  </h4>
                  <p className="text-sm text-gray-500">Training & Placement</p>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2 text-blue-600">
                    <Mail size={16} />
                    <a
                      href="mailto:john.d@college.edu"
                      className="hover:underline"
                    >
                      john.d@college.edu
                    </a>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone size={16} />
                    <span>+1-234-567-8920</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Placed Students (Moved from Right Column) */}
          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
              <User size={18} className="text-gray-700" />
              <h3 className="text-lg font-bold text-gray-900">
                Placed Students ({placement.students.length})
              </h3>
            </div>
            <CardContent className="space-y-6 p-6">
              {placement.students.map((studentId, idx) => (
                <div
                  key={studentId}
                  className="space-y-6 rounded-2xl border border-gray-100 p-6"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 font-bold text-blue-600">
                        <User size={24} />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900">
                          Student {studentId}
                        </h4>
                        <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                          <span>IT202104{idx + 5} • IT</span>
                        </div>
                        <div className="mt-0.5 text-xs text-gray-400">
                          IT 2021-2025
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge className="rounded-full bg-black px-3 px-4 py-1 text-xs uppercase text-white">
                        Confirmed
                      </Badge>
                      <div className="flex items-center gap-1 text-xs font-semibold text-green-600">
                        <CheckCircle2 size={12} /> Offer Letter Received
                      </div>
                    </div>
                  </div>

                  <hr className="border-gray-100" />

                  {/* Main Grid: left col split */}
                  <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                    {/* Academic Performance & Contact */}
                    <div className="space-y-6">
                      <div>
                        <h5 className="mb-3 text-xs font-medium text-gray-500">
                          Academic Performance
                        </h5>
                        <div className="rounded-xl border border-blue-100/50 bg-blue-50/50 p-4">
                          <p className="mb-1 text-xs text-gray-500">CGPA</p>
                          <p className="text-3xl font-bold text-blue-600">
                            8.9
                          </p>
                          <p className="mt-2 text-xs text-gray-500">
                            Backlogs: 0
                          </p>
                        </div>
                      </div>

                      <div>
                        <h5 className="mb-2 text-xs font-medium text-gray-500">
                          Contact
                        </h5>
                        <div className="space-y-2 text-xs text-blue-600">
                          <div className="flex items-center gap-2">
                            <Mail size={12} className="text-gray-400" />
                            <a
                              href={`mailto:student${studentId}@college.edu`}
                              className="hover:underline"
                            >
                              student{studentId}@college.edu
                            </a>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Phone size={12} className="text-gray-400" />
                            <span>+1-234-567-900{idx + 2}</span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h5 className="mb-2 text-xs font-medium text-gray-500">
                          Joining Details
                        </h5>
                        <p className="text-sm font-semibold text-gray-900">
                          {placement.joiningDate
                            ? new Date(
                                placement.joiningDate
                              ).toLocaleDateString("en-GB")
                            : "TBD"}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          Background Check: Completed
                        </p>
                      </div>
                    </div>

                    {/* Selection Process & Skills */}
                    <div className="space-y-6">
                      <div>
                        <h5 className="mb-3 text-xs font-medium text-gray-500">
                          Selection Process
                        </h5>
                        <div className="space-y-4">
                          <div className="relative pl-6">
                            <div className="absolute left-0 top-1 flex h-3 w-3 items-center justify-center rounded-full border-2 border-green-500">
                              <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                            </div>
                            <p className="text-sm font-semibold text-gray-900">
                              Online Test
                            </p>
                            <p className="text-xs italic text-gray-500">
                              Score: 85%
                            </p>
                          </div>
                          <div className="relative pl-6">
                            <div className="absolute left-0 top-1 flex h-3 w-3 items-center justify-center rounded-full border-2 border-green-500">
                              <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                            </div>
                            <p className="text-sm font-semibold text-gray-900">
                              Technical Interview 1
                            </p>
                            <p className="text-xs italic text-gray-500">
                              Strong DSA knowledge
                            </p>
                          </div>
                          <div className="relative pl-6">
                            <div className="absolute left-0 top-1 flex h-3 w-3 items-center justify-center rounded-full border-2 border-green-500">
                              <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                            </div>
                            <p className="text-sm font-semibold text-gray-900">
                              HR Round
                            </p>
                            <p className="text-xs italic text-gray-500">
                              Good communication skills
                            </p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h5 className="mb-3 text-xs font-medium text-gray-500">
                          Skills
                        </h5>
                        <div className="flex flex-wrap gap-1.5">
                          {[
                            "JavaScript",
                            "React",
                            "Node.js",
                            "Python",
                            "SQL",
                            "Git",
                          ].map((s) => (
                            <Badge
                              key={s}
                              variant="secondary"
                              className="bg-gray-100 text-[10px] text-gray-700 hover:bg-gray-200"
                            >
                              {s}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h5 className="mb-2 text-xs font-medium text-gray-500">
                          Certifications
                        </h5>
                        <ul className="space-y-1.5">
                          <li className="flex items-start gap-2 text-xs text-gray-600">
                            <span className="text-orange-500">🏆</span>{" "}
                            Microsoft Certified: Azure Fundamentals
                          </li>
                          <li className="flex items-start gap-2 text-xs text-gray-600">
                            <span className="text-orange-500">🏆</span> AWS
                            Certified Developer - Associate
                          </li>
                        </ul>
                      </div>

                      <div>
                        <h5 className="mb-1 text-xs font-medium text-gray-500">
                          College Mentor
                        </h5>
                        <p className="text-sm font-semibold text-gray-900">
                          Dr. Lisa Anderson
                        </p>
                        <p className="text-xs text-gray-400">IT</p>
                      </div>
                    </div>
                  </div>

                  <hr className="border-gray-100" />

                  {/* Related Training */}
                  <div>
                    <h5 className="mb-3 text-xs font-medium text-gray-500">
                      Related Training & Programs
                    </h5>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div>
                        <p className="mb-2 text-xs text-gray-400">
                          Internships
                        </p>
                        <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-3">
                          <p className="text-xs font-semibold text-gray-900">
                            Data Science Intern
                          </p>
                          <p className="text-[10px] text-gray-500">Microsoft</p>
                        </div>
                      </div>
                      <div>
                        <p className="mb-2 text-xs text-gray-400">
                          Tech Skills
                        </p>
                        <div className="space-y-2">
                          <div className="rounded-xl border border-purple-100 bg-purple-50/50 p-3">
                            <p className="text-xs font-semibold text-gray-900">
                              Machine Learning Bootcamp
                            </p>
                            <p className="text-[10px] text-gray-500">
                              DataScience Hub
                            </p>
                          </div>
                          <div className="rounded-xl border border-purple-100 bg-purple-50/50 p-3">
                            <p className="text-xs font-semibold text-gray-900">
                              Full Stack Web Development
                            </p>
                            <p className="text-[10px] text-gray-500">
                              CodeMaster Institute
                            </p>
                          </div>
                        </div>
                      </div>
                      <div>
                        <p className="mb-2 text-xs text-gray-400">
                          Soft Skills
                        </p>
                        <div className="space-y-2">
                          <div className="rounded-xl border border-yellow-100 bg-yellow-50/50 p-3">
                            <p className="text-xs font-semibold text-gray-900">
                              Effective Communication
                            </p>
                            <p className="text-[10px] text-gray-500">
                              SkillUp Training
                            </p>
                          </div>
                          <div className="rounded-xl border border-yellow-100 bg-yellow-50/50 p-3">
                            <p className="text-xs font-semibold text-gray-900">
                              Time Management
                            </p>
                            <p className="text-[10px] text-gray-500">
                              SkillUp Training
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr className="border-gray-100" />

                  {/* Remarks */}
                  <div>
                    <p className="mb-2 text-xs font-medium text-gray-500">
                      Remarks
                    </p>
                    <p className="text-xs italic text-gray-500">
                      Outstanding student with excellent technical and
                      communication skills. Completed multiple internships and
                      training programs.
                    </p>
                  </div>

                  <Button
                    variant="outline"
                    className="h-11 w-full rounded-xl border-gray-200 text-gray-700 shadow-sm"
                  >
                    View Student Profile
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Omitted as requested, all items now in left col */}
      </div>
    </div>
  );
};

export default ActivityPlacementView;
